import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { keyframes, styled } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CanceledError } from "axios";
import { OrderDirection } from "common/contracts/order-direction";
import PaginationParameters from "common/contracts/pagination-parameters";
import Guid from "common/values/guid/guid";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useSession } from "users/session/session-context";
import CommentThread from "work/entities/comment-thread/comment-thread";
import FeeScheduleTemplateAPIService from "work/entities/fee-schedule-template/api/fee-schedule-template-api-service";
import FeeScheduleTemplateQuery, { 
  FeeScheduleTemplateFilterParameters, 
  FeeScheduleTemplateOrderField, 
  FeeScheduleTemplateOrderParameters 
} from "work/entities/fee-schedule-template/api/request-contracts/query-fee-schedule-template-api-request";
import FeeScheduleTemplate from "work/entities/fee-schedule-template/fee-schedule-template";
import {
  ProposalField,
  ProposalFieldCategory,
} from "work/entities/proposal/proposal";
import ProposalBuilder from "work/entities/proposal/utils/proposal-builder";
import FeeScheduleCategoryName from "work/values/fee-schedule-category-name/fee-schedule-category-name";
import FeeScheduleCategory, {
  FeeScheduleCategoryMaskedCurrency
} from "work/values/fee-schedule-category/fee-schedule-category";
import FeeScheduleCategories from "work/values/fee-schedule-category/view/fee-schedule-categories";
import FeeScheduleTag from "work/values/fee-schedule-tag/fee-schedule-tag";
import ProposalIssues, { ProposalIssueLevel } from "work/values/proposal-issues/proposal-issues";
import { Masked } from "../../../../work-agreement/work-agreement";
import {
  FeeScheduleTagChip,
  TemplateList,
  TemplateLoader
} from "../../../../fee-schedule-template/view/embedded-containers";

const pulseBorder = keyframes`
    0%, 100% {
        box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
        border-color: rgb(21, 101, 192);
        box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
    }
`;
const ListContainer = styled(Grid)(
  () => ({
    minHeight: "20rem",
  }));
const TemplateContainer = styled(Grid)(
  ({ theme }) => ({
    [theme.breakpoints.down("lg")]: {
      width: "100%",
    },
  }));
const CategorySelection = styled(FeeScheduleCategories)(
  ({ theme }) => ({
    [theme.breakpoints.down("lg")]: {
      marginTop: theme.spacing(2),
    },
    height: "100%",
    marginLeft: theme.spacing(2),
  }));
const IssueContainer = styled(
  "div",
  { shouldForwardProp: (prop) => prop !== 'active' }
)<{ active: boolean }>(
  ({ theme, active }) => ({
    animation: active ? `${pulseBorder} 1.5s ease-in-out infinite` : "none",
    margin: active ? theme.spacing(1) : 'initial',
    marginLeft: 0,
    marginBottom: theme.spacing(1),
    padding: active ? theme.spacing(1) : "initial",
    borderRadius: theme.shape.borderRadius,
  }));

type SelectFeeScheduleProps = {
  entityId: Guid;
  activeTab: ProposalFieldCategory;
  activeReviewField?: ProposalField;
  proposalBuilder: ProposalBuilder;
  issues?: ProposalIssues;
  commentsToolTip: string;
  commentThreads?: CommentThread[];
  disableEditing?: boolean;
  onCommentsClicked?: (field: ProposalField, name?: string) => void;
  onProposalBuilderUpdated: (updatedProposalBuilder: ProposalBuilder) => void;
};

export default function FeeScheduleTab(
  props: Readonly<SelectFeeScheduleProps>
) {
  const {
    entityId,
    activeTab,
    activeReviewField,
    proposalBuilder,
    commentThreads,
    commentsToolTip,
    disableEditing,
    onCommentsClicked,
    onProposalBuilderUpdated,
  } = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [templateFeeSchedules, setTemplateFeeSchedules] = useState<
    FeeScheduleTemplate[]
  >([]);
  const [selectedCategories, setSelectedCategories] = useState<
    FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[]
  >([]);
  const [paginatedParameters, setPaginatedParameters] = useState<PaginationParameters>();

  const issues = React.useMemo(
    () => props.issues?.entries.filter(issue => [
      ProposalField.FeeSchedule,
      ProposalField.FeeScheduleCategory(issue.field.id ?? Guid.empty)
    ].includes(issue.field)),
    [props.issues?.entries]
  );

  const isLargeDownDisplay = useMediaQuery((theme) =>
    theme.breakpoints.down("lg")
  );
  const session = useSession();

  useEffect(
    () => {
      if (activeTab !== ProposalFieldCategory.FeeSchedule) return;
      if (Masked.isMasked(proposalBuilder.currentSpec.feeSchedule))
        return;

      initializeFeeSchedule();
      let abortController = new AbortController();
      void getFeeScheduleTemplates(abortController);

      return () => {
        abortController.abort();
        abortController = new AbortController();
      };
    },
    [
      proposalBuilder,
      activeTab
    ]
  );

  function initializeFeeSchedule() {
    if (Masked.isMasked(proposalBuilder.currentSpec.feeSchedule)) {
      throw new Error("Masked fee schedule cannot be edited");
    }
    setSelectedCategories(proposalBuilder.currentSpec.feeSchedule ?? []);
  }

  async function getFeeScheduleTemplates(abortController: AbortController) {
    setIsLoading(true);
    try {
      const filterParams = new FeeScheduleTemplateFilterParameters(entityId);

      const orderParameters = new FeeScheduleTemplateOrderParameters(
        FeeScheduleTemplateOrderField.Name,
        OrderDirection.Descending
      );

      const templateQuery: FeeScheduleTemplateQuery = new FeeScheduleTemplateQuery(
        filterParams,
        orderParameters,
        paginatedParameters ?? new PaginationParameters(0, 100)
      );

      const feeScheduleService = new FeeScheduleTemplateAPIService(session);
      const templates =
        await feeScheduleService.getFeeScheduleTemplates(
          templateQuery,
          abortController
        );
      setTemplateFeeSchedules(templates.data);
    }
    catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
      enqueueSnackbar(
        "Failed to load fee schedule templates.",
        {
          variant: "error",
        }
      );
    } finally {
      setPaginatedParameters((prev) => {
        if (prev) {
          return new PaginationParameters(prev.pageIndex, prev.pageSize);
        }
      });
      setIsLoading(false);
    }
  }

  function handleTemplateExpanded(template: FeeScheduleTemplate) {
    let templates = [...templateFeeSchedules];
    let selectedTemplate = templates.find((feeSchedule) =>
      feeSchedule.id?.isEqualTo(template.id)
    );
    template.isExpanded = !selectedTemplate?.isExpanded;

    setTemplateFeeSchedules(templates);
  }

  function handleTemplateSelectionToggled(template: FeeScheduleTemplate) {
    let updatedSelectedTemplateIds = session.context?.viewingAsVendor
      ? [...proposalBuilder.currentSpec.vendorFeeScheduleTemplateIds]
      : [...proposalBuilder.currentSpec.clientFeeScheduleTemplateIds];
    try {
      if (!template.id) throw new Error("No template selected");


      let updatedProposalBuilder = proposalBuilder;
      if (updatedSelectedTemplateIds.some((id) => id.isEqualTo(template.id))) {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds.filter(
          (id) => !id.isEqualTo(template.id)
        );
      } else {
        updatedSelectedTemplateIds.push(template.id);
        const categories = _.uniqWith(
          [
            ...selectedCategories,
            ...template.categories
          ],
          (a, b) => a.name?.isEqualTo(b.name) ?? false
        );
        updatedProposalBuilder = proposalBuilder.setFeeSchedule(categories);
      }
      updatedProposalBuilder = session.context?.viewingAsVendor
        ? updatedProposalBuilder.setVendorFeeScheduleTemplateIds(
          updatedSelectedTemplateIds
        )
        : updatedProposalBuilder.setClientFeeScheduleTemplateIds(
          updatedSelectedTemplateIds
        );

      onProposalBuilderUpdated(updatedProposalBuilder);
      onCommentsClicked?.(
        ProposalField.FeeSchedule,
        undefined
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to apply template. Please try again",
        {
          variant: "error",
        }
      );
    }
  }

  function handleTemplateCategoryToggled(
    event: React.MouseEvent,
    selectedCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
  ) {
    event.stopPropagation();
    const selectedTemplateIds = session.context?.viewingAsVendor
      ? proposalBuilder.currentSpec.vendorFeeScheduleTemplateIds
      : proposalBuilder.currentSpec.clientFeeScheduleTemplateIds;

    let updatedTemplateIds = session.context?.viewingAsVendor
      ? [...proposalBuilder.currentSpec.vendorFeeScheduleTemplateIds]
      : [...proposalBuilder.currentSpec.clientFeeScheduleTemplateIds];

    if (selectedTemplateIds.some((id) => id.isEqualTo(selectedCategory.id))) {
      updatedTemplateIds = updatedTemplateIds.filter(
        (id) => !id.isEqualTo(selectedCategory.id)
      );
    } else {
      updatedTemplateIds.push(selectedCategory.id);
    }
    let updatedProposalBuilder = session.context?.viewingAsVendor
      ? proposalBuilder.setVendorFeeScheduleTemplateIds(updatedTemplateIds)
      : proposalBuilder.setClientFeeScheduleTemplateIds(updatedTemplateIds);


    let updatedCategories = [...selectedCategories];
    if (
      selectedCategories.some((category) =>
        category.id.isEqualTo(selectedCategory.id)
      )
    ) {
      updatedCategories = updatedCategories.filter(
        (category) => !category.id.isEqualTo(selectedCategory.id)
      );
    } else {
      updatedCategories.push(selectedCategory);
    }
    updatedProposalBuilder = updatedProposalBuilder.setFeeSchedule(
      updatedCategories
    );


    onProposalBuilderUpdated(updatedProposalBuilder);
    onCommentsClicked?.(
      ProposalField.FeeSchedule,
      undefined
    );

  }

  function handleCategoryAdded(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) {
    if (selectedCategories.some((c) => c.isEqualTo(category))) {
      enqueueSnackbar(
        "Fee category is already in the fee schedule",
        { variant: "warning" }
      );
      return;
    }

    const updatedCategories = [
      ...selectedCategories,
      category instanceof FeeScheduleCategoryMaskedCurrency ?
        new FeeScheduleCategoryMaskedCurrency(
          Guid.generate(),
          category.name,
          category.description ?? null,
          category.billingCode ?? null
        ) :
        new FeeScheduleCategory(
          category.name,
          category.description ?? null,
          category.fee,
          category.billingCode ?? null,
          Guid.generate()
        ),
    ];

    onProposalBuilderUpdated(proposalBuilder.setFeeSchedule(updatedCategories));
    onCommentsClicked?.(
      ProposalField.FeeSchedule,
      undefined
    );
  }

  function handleCategoryRemoved(categoryName: FeeScheduleCategoryName) {
    const updatedCategories = selectedCategories.filter(
      (category) => !category.name?.isEqualTo(categoryName)
    );

    onProposalBuilderUpdated(proposalBuilder.setFeeSchedule(updatedCategories));
    onCommentsClicked?.(
      ProposalField.FeeSchedule,
      undefined
    );
  }

  function handleCategoryUpdated(
    originalName: FeeScheduleCategoryName | null,
    updatedCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
  ) {
    if (originalName === null) return;

    if (
      selectedCategories.some((c) => c.isEqualTo(updatedCategory))
    ) {
      enqueueSnackbar(
        "Fee category is already in the fee schedule",
        { variant: "warning" }
      );
      return;
    }

    const updatedCategories = selectedCategories.map((category) => {
      if (category.name?.isEqualTo(originalName)) return updatedCategory;
      return category;
    });

    onProposalBuilderUpdated(proposalBuilder.setFeeSchedule(updatedCategories));
    onCommentsClicked?.(
      ProposalField.FeeSchedule,
      undefined
    );
  }

  function renderTemplates() {
    const selectedTemplateIds = session.context?.viewingAsVendor
      ? proposalBuilder.currentSpec.vendorFeeScheduleTemplateIds
      : proposalBuilder.currentSpec.clientFeeScheduleTemplateIds;
    return (
      <TemplateList subheader={<ListSubheader>Templates</ListSubheader>}>
        {templateFeeSchedules.length === 0 && (
          <>
            {isLoading ? (
              <TemplateLoader size={32} thickness={4} disableShrink />
            ) : (
              <ListItem key="noTemplates">
                <ListItemText>No Templates</ListItemText>
              </ListItem>
            )}
          </>
        )}
        {templateFeeSchedules.map((template: FeeScheduleTemplate) => {
          const templateIsSelected = selectedTemplateIds.some((id) =>
            id.isEqualTo(template.id)
          );

          return (
            <div key={template.id?.toString()}>
              <ListItem
                secondaryAction={
                  <Tooltip
                    title={`${template.isExpanded ? "Hide" : "Show"
                      } Categories`}
                  >
                    <span>
                      <IconButton
                        edge="end"
                        onClick={() => handleTemplateExpanded(template)}
                        size="medium"
                      >
                        {template.isExpanded ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>

                }>
                <ListItemIcon>
                  <Tooltip
                    title={
                      !templateIsSelected
                        ? "Select Template"
                        : "Deselect Template"
                    }
                  >
                    <span>
                      <Checkbox
                        edge="start"
                        color="primary"
                        checked={templateIsSelected}
                        indeterminate={
                          !templateIsSelected &&
                          template.isUsing(selectedCategories)
                        }
                        tabIndex={-1}
                        disableRipple
                        onChange={() =>
                          handleTemplateSelectionToggled(template)
                        }
                      />
                    </span>
                  </Tooltip>
                </ListItemIcon>
                <ListItemText
                  primary={template.name}
                  secondary={template.tags.map((tag: FeeScheduleTag) => (
                    <FeeScheduleTagChip
                      key={tag.value}
                      size="small"
                      label={tag.value}
                    />
                  ))}
                />
              </ListItem>
              <Collapse
                in={template.isExpanded}
                timeout="auto"
                style={{ backgroundColor: "#FAFAFA" }}
              >
                <Divider />
                <List disablePadding dense>
                  {renderTemplateCategories(template)}
                </List>
                <Divider />
              </Collapse>
            </div>
          );
        })}
      </TemplateList>
    );
  }

  function renderTemplateCategories(template: FeeScheduleTemplate) {
    return template.categories.map((category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => {
      const categoryIsSelected = selectedCategories.some((selectedCategory) =>
        selectedCategory.id.isEqualTo(category.id)
      );
      return (
        <ListItem
          key={category.name.value}
          onClick={(event) => handleTemplateCategoryToggled(
            event,
            category
          )}
          secondaryAction={
            <Checkbox
              edge="end"
              color="primary"
              checked={categoryIsSelected}
              tabIndex={-1}
              disableRipple
              onClick={(event) =>
                handleTemplateCategoryToggled(
                  event,
                  category
                )
              }
            />
          }
        >
          <ListItemText
            primary={category.name.value}
            secondary={
              <>
                {category.description?.value}
                <br />
                {category instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" : category.feeString}
              </>
            }
          />
        </ListItem>
      );
    });
  }

  if (activeTab !== ProposalFieldCategory.FeeSchedule) return null;
  if (Masked.isMasked(proposalBuilder.currentSpec.feeSchedule)) {
    return "You do not have permission to view the fee schedule for this proposal.";
  }

  return (
    <ListContainer container direction="row">
      {!disableEditing && (
        <>
          <TemplateContainer>
            {renderTemplates()}
            {isLargeDownDisplay && <Divider />}
          </TemplateContainer>
          {!isLargeDownDisplay && (
            <Grid>
              <Divider orientation="vertical" />
            </Grid>
          )}
        </>
      )}
      <Grid size="grow">
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer
            active={
              issues?.some(issue => issue.field.isEqualTo(activeReviewField)) ?? false
            }
          >
            {issues?.map((issue) => (
              <Typography
                key={issue.field?.key}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
        <CategorySelection
          categories={selectedCategories}
          hideFromTemplateButton={true}
          commentsToolTip={commentsToolTip}
          commentThreads={commentThreads}
          disableEditing={disableEditing}
          activeReviewField={activeReviewField}
          onCommentsClicked={onCommentsClicked}
          onCategoryAdded={handleCategoryAdded}
          onCategoryRemoved={handleCategoryRemoved}
          onCategoryUpdated={handleCategoryUpdated}
        />
      </Grid>
    </ListContainer>
  );
}
